import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/Dialog';
import { ResponsiveSelect } from '../ui/ResponsiveSelect';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';

interface ResumeRunDialogProps {
  open: boolean;
  runId: string;
  onClose: () => void;
  onCompleted?: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  lobby: 'Lobby',
  ingest: 'Ingest',
  triage: 'Triage',
  planning: 'Planning',
  create_hierarchy: 'Hierarchy',
  execute: 'Execute',
  post_execute: 'Post-Execute',
  review: 'Review',
  publish: 'Publish',
  learn: 'Learn',
  complete: 'Complete',
};

function toTitleCase(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function ResumeRunDialog({ open, runId, onClose, onCompleted }: ResumeRunDialogProps) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [runTitle, setRunTitle] = useState('');
  const [stageOptions, setStageOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [targetStage, setTargetStage] = useState('');

  useEffect(() => {
    if (!open || !runId) return;
    setLoading(true);
    setError(null);
    setTargetStage('');
    void apiClient.getAiWorkflowRunDetail(runId)
      .then((data) => {
        const displayTitle = String(data?.summary?.displayTitle || data?.run?.displayTitle || data?.run?.runId || runId);
        setRunTitle(displayTitle);
        const displaySteps = data?.summary?.displaySteps || data?.journal?.displaySteps || {};
        const entries = Object.keys(displaySteps).map((key) => ({
          key,
          status: String(displaySteps[key]?.status || 'pending'),
        }));
        const failedIndex = entries.findIndex((entry) => entry.status === 'failed');
        const eligible = entries.filter((_, index) => (failedIndex >= 0 ? index <= failedIndex : true));
        const options = eligible.map((entry) => ({
          value: entry.key,
          label: STAGE_LABELS[entry.key] || toTitleCase(entry.key),
        }));
        setStageOptions(options);
        if (options.length > 0) {
          setTargetStage(options[options.length - 1].value);
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load run details');
      })
      .finally(() => setLoading(false));
  }, [open, runId]);

  const canSubmit = useMemo(
    () => Boolean(targetStage) && !loading && !submitting,
    [targetStage, loading, submitting],
  );

  const handleResume = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.resumeAiWorkflowRun(runId, targetStage);
      onCompleted?.();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Resume failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <DialogTitle>Resume Run</DialogTitle>
          <DialogDescription>{runTitle || runId}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}
          <div className="space-y-2">
            <label htmlFor="resume-target-stage" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Resume from stage
            </label>
            <ResponsiveSelect
              id="resume-target-stage"
              value={targetStage}
              onChange={setTargetStage}
              options={stageOptions}
              placeholder={loading ? 'Loading stages...' : 'Select stage'}
              disabled={loading || stageOptions.length === 0}
              required
            />
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The pipeline will start from the selected stage and reuse the previously captured state from earlier stages.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleResume} disabled={!canSubmit}>
            {submitting ? 'Resuming...' : 'Resume'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
